// gantt-assignment-bar.component.ts
import * as angular from "angular";
import {
    GanttAssignment,
    GanttList,
    GanttTimelineUnit,
} from "../../../models/gantt.model";

let GanttAssignmentBarComponent = {
    selector: "vcGanttAssignmentBar",
    templateUrl:
        "/angular/components/planner/gantt-chart/gantt-assignment-bar.component.html",
    bindings: {
        assignment: "<", // GanttAssignment
        list: "<", // GanttList (optional, for list bars)
        timeline: "<", // GanttTimelineUnit[]
        cellWidth: "<", // number
        timelineEnd: "<", // Date
        top: "<", // number (for positioning)
        viewMode: "<", // string
        type: "@", // 'assignment' | 'list'
        onClick: "&", // callback function
        hasPermission: "<", // callback function
    },
    controller: class GanttAssignmentBarController {
        public assignment: GanttAssignment;
        public list: GanttList;
        public timeline: GanttTimelineUnit[] = [];
        public cellWidth: number = 60;
        public timelineEnd: Date;
        public top: number = 0;
        public viewMode: "days" | "weeks" | "months" | "quarters" = "days";
        public type: "assignment" | "list" = "assignment";
        public onClick: () => void;
        public hasPermission: boolean;

        constructor() {}

        $onInit() {
            this.cellWidth = this.cellWidth || 60;
            this.viewMode = this.viewMode || "days";
            this.type = this.type || "assignment";
        }

        public getBarStyle(): any {
            if (!this.timeline.length) return { display: "none" };

            let startDate: Date;
            let endDate: Date;

            if (this.type === "list" && this.list) {
                // For list bars, use the range of all assignments
                const assignments = this.list.ganttAssignments;
                startDate = new Date(
                    Math.min(...assignments.map((a) => a.startDate.getTime()))
                );
                endDate = new Date(
                    Math.max(...assignments.map((a) => a.endDate.getTime()))
                );
            } else if (this.assignment) {
                // For assignment bars
                startDate = this.assignment.startDate;
                endDate = this.assignment.endDate;
            } else {
                return { display: "none" };
            }

            // Find the index where the assignment starts (date falls within or before this unit)
            const startIndex = this.timeline.findIndex((unit) =>
                this.dateContainsOrAfter(unit.date, startDate)
            );

            // Find the index where the assignment ends
            let endIndex = -1;
            if (
                this.timelineEnd &&
                this.timelineEnd.getTime() == endDate.getTime()
            ) {
                endIndex = this.timeline.length - 1;
            } else {
                // Find the last unit that contains or is before the end date
                for (let i = this.timeline.length - 1; i >= 0; i--) {
                    if (this.dateContains(this.timeline[i].date, endDate)) {
                        endIndex = i;
                        break;
                    }
                }
                // If not found, find first unit after start that's >= endDate
                if (endIndex === -1 && startIndex !== -1) {
                    for (let i = startIndex; i < this.timeline.length; i++) {
                        if (this.timeline[i].date >= endDate) {
                            endIndex = i;
                            break;
                        }
                    }
                }
            }

            // If we still don't have valid indices, try to find any overlap
            if (startIndex === -1 || endIndex === -1) {
                // Check if there's any overlap at all
                let firstOverlap = -1;
                let lastOverlap = -1;

                for (let i = 0; i < this.timeline.length; i++) {
                    if (
                        this.hasOverlap(
                            this.timeline[i].date,
                            startDate,
                            endDate
                        )
                    ) {
                        if (firstOverlap === -1) firstOverlap = i;
                        lastOverlap = i;
                    }
                }

                if (firstOverlap === -1) {
                    return { display: "none" };
                }

                const duration = lastOverlap - firstOverlap + 1;
                const left = firstOverlap * this.cellWidth;
                const width = duration * this.cellWidth - 1;

                return {
                    left: `${left}px`,
                    width: `${width}px`,
                    height: `30px`,
                    top: `${this.top + 8}px`,
                    position: "absolute",
                    backgroundColor: this.getStatusColor(),
                    zIndex: 1,
                };
            }

            if (endIndex < startIndex) {
                endIndex = startIndex;
            }

            const duration = endIndex - startIndex + 1;
            const left = startIndex * this.cellWidth;
            const width = duration * this.cellWidth - 1;

            return {
                left: `${left}px`,
                width: `${width}px`,
                height: `30px`,
                top: `${this.top + 8}px`,
                position: "absolute",
                backgroundColor: this.getStatusColor(),
                zIndex: 1,
            };
        }

        private dateContainsOrAfter(unitDate: Date, targetDate: Date): boolean {
            switch (this.viewMode) {
                case "days":
                    return (
                        unitDate.toDateString() === targetDate.toDateString()
                    );
                case "weeks":
                    const weekStart = this.getWeekStart(unitDate);
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekEnd.getDate() + 6);
                    return targetDate >= weekStart && targetDate <= weekEnd;
                case "months":
                    return (
                        unitDate.getMonth() === targetDate.getMonth() &&
                        unitDate.getFullYear() === targetDate.getFullYear()
                    );
                case "quarters":
                    const unitQuarter = Math.floor(unitDate.getMonth() / 3);
                    const targetQuarter = Math.floor(targetDate.getMonth() / 3);
                    return (
                        unitQuarter === targetQuarter &&
                        unitDate.getFullYear() === targetDate.getFullYear()
                    );
                default:
                    return false;
            }
        }

        private dateContains(unitDate: Date, targetDate: Date): boolean {
            return this.dateContainsOrAfter(unitDate, targetDate);
        }

        private hasOverlap(
            unitDate: Date,
            startDate: Date,
            endDate: Date
        ): boolean {
            let periodStart: Date;
            let periodEnd: Date;

            switch (this.viewMode) {
                case "days":
                    periodStart = new Date(unitDate);
                    periodEnd = new Date(unitDate);
                    periodEnd.setDate(periodEnd.getDate() + 1);
                    break;
                case "weeks":
                    periodStart = this.getWeekStart(unitDate);
                    periodEnd = new Date(periodStart);
                    periodEnd.setDate(periodEnd.getDate() + 7);
                    break;
                case "months":
                    periodStart = new Date(
                        unitDate.getFullYear(),
                        unitDate.getMonth(),
                        1
                    );
                    periodEnd = new Date(
                        unitDate.getFullYear(),
                        unitDate.getMonth() + 1,
                        1
                    );
                    break;
                case "quarters":
                    const quarter = Math.floor(unitDate.getMonth() / 3);
                    periodStart = new Date(
                        unitDate.getFullYear(),
                        quarter * 3,
                        1
                    );
                    periodEnd = new Date(
                        unitDate.getFullYear(),
                        (quarter + 1) * 3,
                        1
                    );
                    break;
                default:
                    return false;
            }

            // Check if there's any overlap between the period and the assignment
            return startDate < periodEnd && endDate >= periodStart;
        }

        private getWeekStart(date: Date): Date {
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() - day;
            return new Date(d.setDate(diff));
        }

        public getProgressStyle(): any {
            const progress =
                this.type === "list" && this.list
                    ? this.list.overallProgress
                    : this.assignment
                    ? this.assignment.progress
                    : 0;

            return {
                width: `${progress}%`,
                backgroundColor: this.getProgressColor(),
                opacity: 0.3,
            };
        }

        public getDisplayStatus(): string {
            if (this.type === "list") return "List";
            return this.assignment ? this.assignment.status : "Scheduled";
        }

        public getLabelText(): string {
            if (this.type === "list" && this.list) {
                return this.list.gname || "Unnamed List";
            }
            if (this.assignment) {
                // For assignment bars, show team/user assignment info
                return `Assigned to: ${this.assignment.originalAssignment.assigneesString()}`;
            }
            return "";
        }

        public handleBarClick(): void {
            if (this.onClick && this.hasPermission) {
                this.onClick();
            }
        }

        private getStatusColor(): string {
            const status = this.getDisplayStatus();
            const colors = {
                Active: "#15b26d",
                Done: "#2c7297",
                Scheduled: "#ffbf0f",
                List: "#7820c6",
            };
            return colors[status] || "#3498db";
        }

        private getProgressColor(): string {
            const status = this.getDisplayStatus();
            const colors = {
                Active: "#0d7d47",
                Done: "#1c4a5d",
                Scheduled: "#cc9900",
                List: "#5a1a9b",
            };
            return colors[status] || "#2874a6";
        }
    },
};

angular
    .module("planner")
    .component(
        GanttAssignmentBarComponent.selector,
        GanttAssignmentBarComponent
    );
